/**
 * Utility to handle Kenya's Financial Year (July - June)
 * Current month: February 2026 -> Returns FY 2025/2026
 */
export const getFinancialYearData = (date = new Date()) => {
  const month = date.getMonth() + 1; // 1-12
  const year = date.getFullYear();

  // If month is July (7) or later, the FY starts in the current calendar year
  const currentStartYear = month >= 7 ? year : year - 1;

  const formatFY = (startYear) => `FY${startYear}/${Number(startYear) + 1}`;

  return {
    current: formatFY(currentStartYear),
    previous: formatFY(currentStartYear - 1),
    next: formatFY(currentStartYear + 1),
    currentStartYear // useful for API calls
  };
};
/**
 * Converts a datetime string to 'Day DD MMM YYYY' format.
 * Example: '2026-02-22 07:42:55' -> 'Sun 22 Feb 2026'
 */
export const formatDateTime = (inputString) => {
  // Replace space with 'T' for robust cross-browser ISO parsing
  const date = new Date(inputString.replace(' ', 'T'));

  const options = { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  };

  // 'en-GB' naturally orders parts as: [Weekday] [Day] [Month] [Year]
  const formatter = new Intl.DateTimeFormat('en-GB', options);
  
  // .format() returns 'Sun, 22 Feb 2026'; we remove the comma
  return formatter.format(date).replace(',', '');
}
/**
 * Converts a datetime string to 'Day DD MMM YYYY' format.
 * Example: '2026-02-22 07:42:55' -> 'Sun 22 Feb 2026 07:42 AM'
 */
export const formatDateTime2 = (inputString) => {
  // Replace space with 'T' for robust cross-browser ISO parsing
  const date = new Date(inputString.replace(' ', 'T'));

  const options = { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true 
  };

  // 'en-GB' naturally orders parts as: [Weekday] [Day] [Month] [Year]
  const formatter = new Intl.DateTimeFormat('en-GB', options);
  
  // .format() returns 'Sun, 22 Feb 2026'; we remove the comma
  return formatter.format(date).replace(',', '');
}
/**
 * Converts a UTC MySQL/MariaDB datetime string to Africa/Nairobi time.
 * @param {string} dateString - Format: "2026-02-27 20:34:49"
 * @returns {string} - Localized string for Nairobi
 */
export const formatToLocalTime = (dateString) => {
  if (!dateString) return 'N/A';

  // 1. Force the string to be interpreted as UTC by appending 'Z'
  // MySQL 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ssZ'
  const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');

  // 2. Format specifically for local user timezone. 
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(utcDate);
};
export const formatToLocalTime3 = (dateString) => {
  if (!dateString) return 'N/A';

  // Convert MySQL string → ISO UTC string
  const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Nairobi',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(utcDate);
};
// dateHelper.js
export const formatToLocalTime2 = (dateString) => {
  if (!dateString) return 'N/A';

  // The backend already provides: 2026-03-12T23:13:44.000Z
  // New Date() handles this format natively without manual string manipulation
  const date = new Date(dateString);

  // Check if the date is valid to prevent the RangeError
  if (isNaN(date.getTime())) {
    console.warn("Invalid date value received:", dateString);
    return 'Invalid Date';
  }

  // Format for local user timezone (e.g., East Africa Time)
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(date);
};
// Returns "Sat 28 Feb 2026"
export const formatTodayTitle = (dateString) => {
  if (!dateString) return 'N/A';

  // 1. Force the string to be interpreted as UTC by appending 'Z'
  // MySQL 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ssZ'
  const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(utcDate);
};
export const formatTodayTitle2 = (dateString) => {
  if (!dateString) return 'N/A';

  // 1. Force the string to be interpreted as UTC by appending 'Z'
  // MySQL 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ssZ'
  //const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
  const utcDate = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(utcDate);
};

// Returns "February - 2026"
export const formatCurrentMonthTitle = (dateString) => {
  if (!dateString) return 'N/A';

  // 1. Force the string to be interpreted as UTC by appending 'Z'
  // MySQL 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ssZ'
  const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: 'numeric'
  }).format(utcDate).replace(' ', ' - ');
};
export const formatCurrentMonthTitle2 = (dateString) => {
  if (!dateString) return 'N/A';

  // 1. Force the string to be interpreted as UTC by appending 'Z'
  // MySQL 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ssZ'
  //const utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
  const utcDate = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: 'numeric'
  }).format(utcDate).replace(' ', ' - ');
};
/**
 * Extracts the start year as a number from a Financial Year string
 * @param {string} fyString - Format: "FY2025/2026"
 * @returns {number} - 2025
 */
export const getFYStartYear = (fyString) => {
  if (!fyString) return new Date().getFullYear();
  
  // Matches the first 4 consecutive digits in the string
  const match = fyString.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : new Date().getFullYear();
};

export const getFYEndYear = (fyString) => {
  if (!fyString) return new Date().getFullYear();

  // Matches the second set of 4 consecutive digits in the string
  const match = fyString.match(/\/(\d{4})/);
  return match ? parseInt(match[1], 10) : new Date().getFullYear();
};
